import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import DeviceLoadingSkeleton from '../shared-ui/deviceLoadingSkeleton';
import { formatDeviceMetric } from '../../../utils/formatters';
import {
  useWindManagementSystemDetailQuery,
  useWindManagementSystemDetailUpdatesSubscription,
} from '../../../../types/generated-types';
import { PanelFrame, ViewMode } from '../shared-ui/panel-frame';
import moment from 'moment';
import { DeviceInformation } from '../shared-ui/device-info';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import Divider from '@mui/material/Divider';
import { EnergyRectifierComponent } from './energy-rectifier';
import Stack from '@mui/material/Stack';

interface WindManagementSystemProps {
  id: string;
}

export const WindManagementSystemComponent: React.FC<
  WindManagementSystemProps
> = ({ id }) => {
  const [device, setDevice] = React.useState<any>();
  const { data, loading, error } = useWindManagementSystemDetailQuery({
    variables: { id },
  });

  useWindManagementSystemDetailUpdatesSubscription({
    variables: { id },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  React.useEffect(() => {
    if (data?.windManagementSystemById) {
      setDevice(data.windManagementSystemById);
    }
  }, [data]);

  if (loading) return <DeviceLoadingSkeleton size="large" />;
  if (error)
    return <Typography color="error">Error loading device data</Typography>;
  if (!device) return null;

  const deviceInfo: DeviceInformation[] = [
    {
      'System Name': device.name || 'Unnamed Wind Management System',
      Location: device.location || 'Unknown',
      'Last Updated': moment(device.timestamp).fromNow(),
      'Wind Speed': formatDeviceMetric({
        value: device.windSpeed?.value ?? undefined,
        units: device.windSpeed?.units ?? undefined,
      }),
      'Wind Direction': formatDeviceMetric({
        value: device.windDirection?.value ?? undefined,
        units: device.windDirection?.units ?? undefined,
      }),
      'Rotor Speed': formatDeviceMetric({
        value: device.rotorSpeed?.value ?? undefined,
        units: device.rotorSpeed?.units ?? undefined,
      }),
      'Pitch Angle': formatDeviceMetric({
        value: device.pitchAngle?.value ?? undefined,
        units: device.pitchAngle?.units ?? undefined,
      }),
      'Yaw Control Status': device.yawControlStatus?.value ?? 'Unknown',
      'Vibration Level': formatDeviceMetric({
        value: device.vibrationLevel?.value ?? undefined,
        units: device.vibrationLevel?.units ?? undefined,
      }),
      'Generator Temperature': formatDeviceMetric({
        value: device.generatorTemperature?.value ?? undefined,
        units: device.generatorTemperature?.units ?? undefined,
      }),
      'Brake Status': device.brakeStatus?.value ? 'Engaged' : 'Disengaged',
      'Blade Condition': device.bladeCondition?.value
        ? device.bladeCondition.value
        : 'Unknown',
      'Ice Detection Status': device.iceDetectionStatus?.value
        ? device.iceDetectionStatus.value
        : 'Unknown',
      Property: device.property?.name || 'Not assigned',
      Unit: device.unit?.name || 'Not assigned',
      Panel: device.panel?.name || 'Not assigned',
      Source: device.source?.name || 'Not assigned',
    },
  ];

  return (
    <PanelFrame
      title="Wind Management System"
      iconType="mdi:wind-turbine"
      panel={device}
      infoEntries={deviceInfo}
      viewMode={ViewMode.NORMAL}
      editingEnabled={false}
    >
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Wind Metrics
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Wind Speed
                </Typography>
                <Typography variant="body1">
                  {formatDeviceMetric({
                    value: device.windSpeed?.value ?? undefined,
                    units: device.windSpeed?.units ?? undefined,
                  })}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Wind Direction
                </Typography>
                <Typography variant="body1">
                  {formatDeviceMetric({
                    value: device.windDirection?.value ?? undefined,
                    units: device.windDirection?.units ?? undefined,
                  })}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Rotor Speed
                </Typography>
                <Typography variant="body1">
                  {formatDeviceMetric({
                    value: device.rotorSpeed?.value ?? undefined,
                    units: device.rotorSpeed?.units ?? undefined,
                  })}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Pitch Angle
                </Typography>
                <Typography variant="body1">
                  {formatDeviceMetric({
                    value: device.pitchAngle?.value ?? undefined,
                    units: device.pitchAngle?.units ?? undefined,
                  })}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              System Status
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Yaw Control
                </Typography>
                <Typography variant="body1">
                  {device.yawControlStatus?.value ?? 'Unknown'}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Vibration Level
                </Typography>
                <Typography variant="body1">
                  {formatDeviceMetric({
                    value: device.vibrationLevel?.value ?? undefined,
                    units: device.vibrationLevel?.units ?? undefined,
                  })}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Generator Temperature
                </Typography>
                <Typography variant="body1">
                  {formatDeviceMetric({
                    value: device.generatorTemperature?.value ?? undefined,
                    units: device.generatorTemperature?.units ?? undefined,
                  })}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Brake Status
                </Typography>
                <Typography variant="body1">
                  {device.brakeStatus?.value ? 'Engaged' : 'Disengaged'}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </PanelFrame>
  );
};
